import type { FormEvent, ReactNode } from 'react'

// Shared modal + form primitives. All app-level modals (EditEventModal,
// NewEventModal, ActivityEditor, and future Components/Checklists/etc
// editors) build on this. WHY: the rgba-overlay, white card, label-above-
// input pattern is the prototype's editor language - establishing it once
// here keeps every editor in the app visually identical for free.
//
// Tokens:
//   overlay      rgba(31,29,24,0.55) + backdrop-blur(4px)
//   card         max-w-{size} bg-white rounded-lg shadow-xl
//   field-label  uppercase 11.5/0.08em ink-soft tracking-wide font-semibold
//   input        border-line / focus border-green / error border-oxblood
//   primary btn  bg-green text-parchment

type ModalSize = 'lg' | 'xl' | '2xl'

const sizeClass: Record<ModalSize, string> = {
  'lg':  'max-w-lg',
  'xl':  'max-w-xl',
  '2xl': 'max-w-2xl'
}

// ModalShell - the overlay + card wrapper + header. Body and footer are
// passed in as ReactNodes so each editor controls its own form layout.
// onSubmit makes the card a <form>, so a primary button with type="submit"
// in the footer triggers it (and Enter from any input does the same -
// browser default).
export function ModalShell({
  title,
  onClose,
  onSubmit,
  body,
  footer,
  size = 'xl'
}: {
  title: string
  onClose: () => void
  onSubmit?: (e: FormEvent) => void
  body: ReactNode
  footer?: ReactNode
  size?: ModalSize
}) {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit ?? ((e) => e.preventDefault())}
        className={`bg-white rounded-lg shadow-xl w-full ${sizeClass[size]} my-8 p-7`}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-soft hover:text-ink text-xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-parchment"
          >
            ×
          </button>
        </div>
        {body}
        {footer && (
          <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-line">
            {footer}
          </div>
        )}
      </form>
    </div>
  )
}

// Field - a labelled wrapper for a single form control. `hint` shows on the
// right of the label (e.g. "Leave blank for no cap" or a live value preview);
// `error` shows below in oxblood. Children render between label and error.
export function Field({
  label,
  hint,
  error,
  children
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
          {label}
        </span>
        {hint && <span className="text-xs text-ink-soft font-mono">{hint}</span>}
      </div>
      {children}
      {error && <div className="text-xs text-oxblood mt-1">{error}</div>}
    </label>
  )
}

// inputClass - returns the className string for a form control. The error
// variant swaps the border + focus-ring to oxblood. Extracted so every
// input/select/textarea in the app gets the same focus/error treatment.
export function inputClass(hasError: boolean): string {
  const base =
    'w-full border rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 transition-colors'
  return hasError
    ? `${base} border-oxblood focus:border-oxblood focus:ring-oxblood/30`
    : `${base} border-line focus:border-green focus:ring-green/30`
}

// FormStatus - inline info/error block for use inside a modal body when
// loading data, recovering from a failure, or surfacing a server-side error.
// Sized to sit comfortably alongside form fields.
export function FormStatus({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'error' }) {
  const cls =
    tone === 'error'
      ? 'bg-oxblood-soft border-oxblood text-oxblood'
      : 'bg-parchment border-line text-ink-soft'
  return (
    <div className={`${cls} border rounded px-3 py-3 text-sm`}>
      {children}
    </div>
  )
}

// ServerError - standard rendering of a TanStack mutation error (or any
// thrown Error) inside a modal body. Used by every editor's submit path so
// failures look the same everywhere.
export function ServerError({ title, error }: { title: string; error: Error }) {
  return (
    <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-xs">
      <div className="font-semibold mb-0.5">{title}</div>
      <pre className="whitespace-pre-wrap font-mono">{error.message}</pre>
    </div>
  )
}

// CancelButton, PrimaryButton, DangerButton - footer buttons. Defined here
// so the visual language of "Cancel / Save / Delete" is consistent across
// every modal in the app.
export function CancelButton({
  onClick, disabled, children = 'Cancel'
}: {
  onClick: () => void
  disabled?: boolean
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}

export function PrimaryButton({
  type = 'submit', onClick, disabled, children
}: {
  type?: 'submit' | 'button'
  onClick?: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}

// DangerButton - oxblood text on hover-soft background. Used for Delete
// actions inside an editor footer. mr-auto pushes it to the left of Cancel/
// Primary when included as the first footer child.
export function DangerButton({
  onClick, disabled, children = 'Delete'
}: {
  onClick: () => void
  disabled?: boolean
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mr-auto px-4 py-2 rounded text-sm text-oxblood hover:bg-oxblood-soft disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}
